import { useEffect, useRef } from 'react'
import { useLocation } from 'react-router-dom'
import styles from './LogoMark.module.css'

interface Props {
  size?: number
  className?: string
}

type Direction = 'b2f' | 'f2b'

/**
 * Brand mark for DepthZone: three layered mountain silhouettes with
 * atmospheric perspective (far = pale, near = deep). The gradient
 * from back to front is itself a hand-drawn depth map.
 *
 * Plays a staggered "wave" animation on mount and re-triggers it on
 * every route change — each <path> takes `key={location.pathname}` so
 * React remounts them, restarting the CSS animation from scratch.
 *
 * The stagger order alternates each navigation:
 *  - `b2f` (back-to-front): far → mid → near
 *  - `f2b` (front-to-back): near → mid → far
 *
 * Reused by Header, Footer, and (rasterized) by the favicon.
 */
export default function LogoMark({ size = 28, className }: Props) {
  const location = useLocation()
  const k = location.pathname

  // Direction is held in a ref to avoid an extra re-render after each
  // navigation. The current render uses the value set by the previous
  // navigation; useEffect flips it for the next one.
  const directionRef = useRef<Direction>('b2f')
  useEffect(() => {
    directionRef.current = directionRef.current === 'b2f' ? 'f2b' : 'b2f'
  }, [k])

  const dirClass = directionRef.current === 'b2f' ? styles.b2f : styles.f2b

  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 28 28"
      fill="none"
      role="img"
      aria-label="DepthZone"
      className={`${styles.root} ${dirClass}${className ? ` ${className}` : ''}`}
    >
      <rect width="28" height="28" rx="7" fill="#1a4d35" opacity="0.25" />
      <path
        key={`far-${k}`}
        className={styles.mountainFar}
        d="M0 24 L8 8 L14 14 L22 4 L28 24 Z"
        fill="#86efac"
        opacity="0.45"
      />
      <path
        key={`mid-${k}`}
        className={styles.mountainMid}
        d="M0 24 L5 16 L11 19 L18 12 L25 18 L28 24 Z"
        fill="#3da86b"
        opacity="0.85"
      />
      <path
        key={`near-${k}`}
        className={styles.mountainNear}
        d="M0 24 L6 20 L12 22 L17 19 L24 21 L28 24 Z"
        fill="#1f6b43"
      />
    </svg>
  )
}
